import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { eventApi, categoriesApi } from '../services/api';
import { toast } from 'sonner';
import { Calendar, Upload, X, Star } from 'lucide-react';

const SubmitEvent = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isCommunityMember, isBusinessOwner, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [form, setForm] = useState({
    event_name: '',
    category: '',
    event_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    location: '',
    description: '',
    ticket_url: '',
    cost: '',
    submitter_name: '',
    organization_name: ''
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || (!isCommunityMember && !isBusinessOwner)) {
      navigate('/community-login');
      return;
    }
    loadCategories();
    // Pre-fill submitter name
    if (user?.name) {
      setForm(f => ({ ...f, submitter_name: user.name }));
    }
  }, [isAuthenticated, isCommunityMember, isBusinessOwner, authLoading, navigate, user]);

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getEventCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setImage(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.description.length > 500) {
      toast.error('Description must be 500 characters or less');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('event_name', form.event_name);
      formData.append('category', form.category);
      formData.append('event_date', form.event_date);
      formData.append('end_date', form.end_date || form.event_date);
      formData.append('start_time', form.start_time);
      formData.append('end_time', form.end_time);
      formData.append('location', form.location);
      formData.append('description', form.description);
      formData.append('submitter_name', form.submitter_name);
      
      if (form.ticket_url) formData.append('ticket_url', form.ticket_url);
      if (form.cost) formData.append('cost', form.cost);
      if (form.organization_name) formData.append('organization_name', form.organization_name);
      if (image) formData.append('image', image);
      
      await eventApi.create(formData);
      toast.success('Event submitted for approval!');
      
      navigate(isBusinessOwner ? '/business-account' : '/community-account');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 py-8" data-testid="submit-event-page">
      <Helmet>
        <title>Submit a Community Event | Martinsville & Henry County, VA | Explore MHC</title>
        <meta name="description" content="Share your event with the Martinsville and Henry County community. Submit festivals, markets, fundraisers, and local gatherings on Explore MHC." />
      </Helmet>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Submit an Event
          </h1>
          <p className="text-neutral-600">
            Share your event with the Martinsville and Henry County community
          </p>
          <p className="text-sm text-neutral-500 mt-2">
            Free listing included. Want extra visibility? <Link to="/pricing" className="text-primary font-medium hover:underline">See featured placement options</Link> starting at $10/week.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <div className="form-section">
            <h2 className="form-section-title">Event Details</h2>
            
            <div className="space-y-6">
              <div>
                <label className="form-label">Event Name *</label>
                <input
                  type="text"
                  required
                  value={form.event_name}
                  onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                  className="form-input"
                  data-testid="event-name-input"
                />
              </div>
              
              <div>
                <label className="form-label">Category *</label>
                <select
                  required
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="form-input"
                  data-testid="event-category-select"
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Start Date *</label>
                  <input
                    type="date"
                    required
                    value={form.event_date}
                    onChange={(e) => setForm({ ...form, event_date: e.target.value, end_date: form.end_date || e.target.value })}
                    className="form-input"
                    data-testid="event-date-input"
                  />
                </div>

                <div>
                  <label className="form-label">End Date * <span className="text-neutral-400 font-normal text-sm">(event hidden after this date)</span></label>
                  <input
                    type="date"
                    required
                    value={form.end_date}
                    min={form.event_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="form-input"
                    data-testid="event-end-date-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Start Time *</label>
                  <input
                    type="time"
                    required
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                    className="form-input"
                    data-testid="event-start-time-input"
                  />
                </div>
                
                <div>
                  <label className="form-label">End Time *</label>
                  <input
                    type="time"
                    required
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    className="form-input"
                    data-testid="event-end-time-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="form-label">Location *</label>
                <input
                  type="text"
                  required
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  className="form-input"
                  placeholder="Full address or 'Online'"
                  data-testid="event-location-input"
                />
              </div>
              
              <div>
                <label className="form-label">Description * ({form.description.length}/500 characters)</label>
                <textarea
                  required
                  maxLength={500}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="form-textarea"
                  rows={4}
                  data-testid="event-description-textarea"
                />
              </div>
            </div>
          </div>

          {/* Optional Info */}
          <div className="form-section">
            <h2 className="form-section-title">Additional Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Ticket/Registration URL</label>
                <input
                  type="url"
                  value={form.ticket_url}
                  onChange={(e) => setForm({ ...form, ticket_url: e.target.value })}
                  className="form-input"
                  placeholder="https://..."
                />
              </div>
              
              <div>
                <label className="form-label">Cost</label>
                <input
                  type="text"
                  value={form.cost}
                  onChange={(e) => setForm({ ...form, cost: e.target.value })}
                  className="form-input"
                  placeholder="Free or $10"
                />
              </div>
            </div>
          </div>

          {/* Event Image */}
          <div className="form-section">
            <h2 className="form-section-title">Event Image/Flyer (Optional)</h2>
            
            {imagePreview ? (
              <div className="relative inline-block">
                <img 
                  src={imagePreview} 
                  alt="Event preview" 
                  className="max-w-sm rounded-xl"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <label className="block w-full max-w-sm aspect-video border-2 border-dashed border-neutral-200 rounded-xl cursor-pointer hover:border-primary transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <div className="h-full flex flex-col items-center justify-center">
                  <Upload className="w-12 h-12 text-neutral-400 mb-3" />
                  <span className="text-neutral-600 font-medium">Upload Image</span>
                  <span className="text-neutral-400 text-sm">PNG, JPG up to 5MB</span>
                </div>
              </label>
            )}
          </div>

          {/* Submitter Info */}
          <div className="form-section">
            <h2 className="form-section-title">Submitter Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Your Name *</label>
                <input
                  type="text"
                  required
                  value={form.submitter_name}
                  onChange={(e) => setForm({ ...form, submitter_name: e.target.value })}
                  className="form-input"
                  data-testid="submitter-name-input"
                />
              </div>
              
              <div>
                <label className="form-label">Organization Name (Optional)</label>
                <input
                  type="text"
                  value={form.organization_name}
                  onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="form-section">
            <h2 className="form-section-title">Featured Placement (Optional)</h2>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
              <div className="flex items-start gap-3 mb-3">
                <Star className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-neutral-900">Want your event to stand out?</h3>
                  <p className="text-sm text-neutral-600 mt-1">
                    Featured events appear at the top of listings, on the homepage, and get priority placement across the site.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <div className="bg-white rounded-lg border border-amber-200 p-4">
                  <p className="font-bold text-neutral-900">$10<span className="text-sm font-normal text-neutral-500">/week</span></p>
                  <p className="text-xs text-neutral-500">Featured Event Weekly</p>
                </div>
                <div className="bg-white rounded-lg border border-amber-200 p-4">
                  <p className="font-bold text-neutral-900">$25<span className="text-sm font-normal text-neutral-500"> flat</span></p>
                  <p className="text-xs text-neutral-500">Featured Until Event End Date</p>
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-3">
                Submit your event first for approval. Once approved, you can purchase featured placement from your dashboard.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1"
              disabled={loading}
              data-testid="submit-event-btn"
            >
              {loading ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmitEvent;
